import Quill from 'quill';
import { CSS_CLASSES } from '../../constants/CSSClasses';
import { Section } from './TextSection';
import { SectionBase } from './SectionBase';
import 'quill/dist/quill.snow.css';
import { changeType, StateManager } from '../../store/StateManager';
import { DomHelper } from '../../utlis/DomHelper';
import './richTextSection.css';
import { QuillCSS } from './quillCSS';

var Parchment = Quill.import('parchment');
const config = {
    scope: Parchment.Scope.BLOCK,
    whitelist: ['0.5', '0.6', '0.7', '0.8', '0.9', '1', '1.1', '1.2', '1.3', '1.5', '1.7', '2', "3", "4", "5", "6"]
}
var lineHtClass = new Parchment.Attributor.Class('lineheight', 'ql-line-height', config);
var lineHtStyle = new Parchment.Attributor.Style('lineheight', 'line-height', config);

Parchment.register(lineHtClass);
Parchment.register(lineHtStyle);

export class RichTextSection extends SectionBase {
    layoutDesignerClasses: Record<string, Record<string, string>>
    constructor(options: Section) {
        super(options);
        this.layoutDesignerClasses = {}
        this.fetchCssClassesAndProperties().then(cssData => {
            this.layoutDesignerClasses = cssData;
        });
    }

    clone() {
        return new RichTextSection(this.toJSON());
    }

    render(): HTMLDivElement {
        const parent = document.createElement('div');
        parent.classList.add(CSS_CLASSES.WRAPPER);

        const div = document.createElement('div');
        div.classList.add(CSS_CLASSES.SECTION);
        div.classList.add(CSS_CLASSES.RICH_TEXT_SECTION);
        div.style.position = 'relative';
        div.style.overflow = 'auto';
        div.innerHTML = this.textContent;

        const editIcon = document.createElement('span');
        editIcon.title = 'Edit Rich Text';
        editIcon.classList.add(CSS_CLASSES.EDIT_ICON);
        editIcon.textContent = '✎';
        Object.assign(editIcon.style, {
            position: 'absolute',
            top: '1px',
            right: '1px',
            cursor: 'pointer'
        });

        editIcon.addEventListener('click', () => {
            this.openEditor(div);
        });

        if (this.selected) {
            div.classList.add(CSS_CLASSES.SELECTED);
        }

        parent.appendChild(div);
        parent.appendChild(editIcon);
        return parent;

    }

    addCustomDropDownForAlignOption() {
        // Add custom SVG icon for ql-align
        const alignContainer = document.querySelector('.ql-align.ql-picker');
        alignContainer && alignContainer.classList.add('custom-align-container');
        const alignSelect: any = document.querySelector('.ql-align .ql-picker-label');
        if (alignSelect) {
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('viewBox', '0 0 18 18');
            svg.classList.add('custom-dropdown-icon');
            svg.style.width = '18px';
            svg.style.height = '18px';
            svg.style.position = 'absolute';
            svg.style.right = '-30px';
            svg.style.top = '50%';
            svg.style.transform = 'translateY(-50%)';
            svg.style.pointerEvents = 'none';
            svg.style.zIndex = '5';
            svg.style.marginRight = '10px';

            const polygon1 = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
            polygon1.setAttribute('points', '7 11 9 13 11 11 7 11');
            polygon1.classList.add('ql-stroke');

            const polygon2 = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
            polygon2.setAttribute('points', '7 7 9 5 11 7 7 7');
            polygon2.classList.add('ql-stroke');

            svg.appendChild(polygon1);
            svg.appendChild(polygon2);

            alignSelect.parentElement!.style.position = 'relative';
            alignSelect.parentElement!.appendChild(svg);

            // Style the select element
            Object.assign(alignSelect.style, {
                paddingRight: '20px',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                background: '#fff',
                borderRadius: '4px',
                width: '50px',
                cursor: 'pointer'
            });
        }
    }

    getFontSizeTooltips(): Record<string, string> {
        const fontSizes = this.getFontArray(); // e.g., ['7px', '8px', ..., '100px']
        const tooltips: Record<string, string> = {};
        fontSizes.forEach(size => {
            tooltips[size] = `Font Size: ${size}`;
        });
        return tooltips;
    }

    getFontFamilyTooltips(): Record<string, string> {
        const fontFamilies = [
            'Primer Print', 'Avenir Next LT Pro', 'Cooper Black',
            'Teachers Pet', 'Papyrus', 'monospace', 'Arial', 'Times New Roman',
            'Courier New', 'Georgia', 'Verdana', 'Trebuchet MS', 'Comic Sans MS',
            'Impact', 'Garamond', 'Palatino Linotype', 'Tahoma', 'Parchment',
            'Poor Richard', 'Black Ladder', 'Ravie', 'Bauhaus', 'Brush Script MT',
        ];
        const tooltips: Record<string, string> = {};
        fontFamilies.forEach(font => {
            tooltips[font] = `Font Family: ${font}`;
        });
        return tooltips;
    }

    getLineHeightTooltips(): Record<string, string> {
        const lineHeights = ['0.5', '0.6', '0.7', '0.8', '0.9', '1', '1.1', '1.2', '1.3', '1.5', '1.7', '2', '3', '4', '5', '6'];
        const tooltips: Record<string, string> = {};
        lineHeights.forEach(height => {
            tooltips[height] = `Line Height: ${height}`;
        });
        return tooltips;
    }

    addQuillTooltips() {
        const tooltips: Record<string, string> = {
            '.ql-bold': 'Bold text',
            '.ql-italic': 'Italicize text',
            '.ql-underline': 'Underline text',
            '.ql-strike': 'Strike through text',
            '.ql-blockquote': 'Blockquote text',
            '.ql-code-block': 'Code block text',
            '.ql-link': 'Insert hyperlink',
            '.ql-list[value="ordered"]': 'Ordered list',
            '.ql-list[value="bullet"]': 'Bullet list',
            '.ql-indent[value="-1"]': 'Decrease indent',
            '.ql-indent[value="+1"]': 'Increase indent',
            '.ql-direction[value="rtl"]': 'Right-to-left text direction',
            '.ql-size': 'Font Size',
            '.ql-header': 'Header size',
            '.ql-color': 'Text color',
            '.ql-background': 'Background color',
            '.ql-font': 'Font family',
            '.ql-align': 'Text alignment',
            '.ql-clean': 'Remove formatting',
            '.ql-lineHeight': 'Line Height',
            '.ql-script[value="sub"]': 'SubScript',
            '.ql-script[value="super"]': 'SuperScript',
            '.ql-image': 'Image',
        };

        Object.entries(tooltips).forEach(([selector, tooltip]) => {
            const elements = document.querySelectorAll(`.ql-toolbar ${selector}`);
            elements.forEach(el => {
                el.setAttribute('title', tooltip)
            });
        });

        // Tooltips for dropdown options (picker items)
        const dropdownTooltips: Record<string, Record<string, string>> = {
            '.ql-size': this.getFontSizeTooltips(),
            '.ql-font': this.getFontFamilyTooltips(),
            '.ql-lineHeight': this.getLineHeightTooltips(),
            '.ql-align': {
                '': 'Text alignment: Left Align',
                'center': 'Text alignment: Center Align',
                'right': 'Text alignment: Right Align',
                'justify': 'Text alignment: Justify Align',
            },
            '.ql-header': {
                '1': 'Header Size: Heading 1',
                '2': 'Header Size: Heading 2',
                '3': 'Header Size: Heading 3',
                '': 'Header Size: Normal Text',
            },
        };

        // Apply tooltips to dropdown options
        Object.entries(dropdownTooltips).forEach(([pickerSelector, tooltips]) => {
            const picker = document.querySelector(`.ql-toolbar ${pickerSelector}`);
            if (picker) {
                const items = picker.querySelectorAll('.ql-picker-item');
                items.forEach(item => {
                    const value = item.getAttribute('data-value') || '';
                    const tooltip = tooltips[value] || 'Unknown Option';
                    item.setAttribute('title', tooltip);
                });
            }
        });
    }

    openEditor(div: HTMLDivElement) {

        // get parent where the dialog element would be added
        const parentElement = document.querySelector("#layout-builder");
        if (!parentElement)
            return;
        // Disable body scroll
        document.body.style.overflow = 'hidden';

        // Create overlay backdrop
        const backdrop = document.createElement('div');
        Object.assign(backdrop.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: '99998' // behind dialog
        });

        const dialog = document.createElement('div');
        dialog.classList.add(CSS_CLASSES.EDITOR_DIALOG);
        Object.assign(dialog.style, {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            padding: '20px',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            zIndex: '99999',
            width: '80%',
            maxWidth: '800px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
        });

        const toolbarContainer = document.createElement('div');
        const editorContainer = document.createElement('div');
        Object.assign(editorContainer.style, {
            height: '400px',
            marginBottom: '20px',
            border: '1px solid #ccc',
            borderRadius: '4px'
        });

        dialog.appendChild(toolbarContainer);
        dialog.appendChild(editorContainer);
        parentElement.appendChild(backdrop);
        parentElement.appendChild(dialog);


        const fontFamilyOptions = [
            "Primer Print", 
            "Avenir Next LT Pro",
            "Cooper Black", "Teachers Pet", "Papyrus",
            "monospace", "Arial", "Times New Roman", "Courier New",
            "Georgia", "Verdana", "Trebuchet MS", "Comic Sans MS", "Impact", "Garamond", "Palatino Linotype",
            "Tahoma", "Parchment", "Poor Richard", "Black Ladder", "Ravie", "Bauhaus", "Brush Script MT"
        ];
        const Font = Quill.import('attributors/style/font');
        (Font as any).whitelist = fontFamilyOptions;
        Quill.register('formats/font', Font);
        var Size = Quill.import('attributors/style/size');
        const fontSizeArr = this.getFontArray();
        Size.whitelist = fontSizeArr;
        Quill.register(Size as any, true);

        const LineHeight: any = Quill.import('blots/block');


        // For Align Style
        const AlignStyle = Quill.import('attributors/style/align');
        Quill.register(AlignStyle, true);

        class LineHeightBlot extends LineHeight {
            static blotName = 'lineHeight';
            static tagName = 'p'; // or 'div' depending on your use case

            static formats(domNode: HTMLElement) {
                const lineHeight = domNode.style.lineHeight;
                return lineHeight ? lineHeight : undefined;
            }

            format(name: string, value: string) {
                if (name === 'lineHeight') {
                    this.domNode.style.lineHeight = value;
                } else {
                    super.format(name, value);
                }
            }

            // Ensure list attributes are retained
            static applyToList(node: HTMLElement, value: string) {
                if (node.tagName === 'LI') {
                    node.style.lineHeight = value;
                } else {
                    const parent = node.closest('ul, ol');
                    if (parent) {
                        parent.querySelectorAll('li').forEach(li => {
                            li.style.lineHeight = value;
                        });
                    }
                }
            }
        }

        Quill.register(LineHeightBlot);

        // line height 
        const quill = new Quill(editorContainer, {
            theme: 'snow',
            modules: {
                toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'font': fontFamilyOptions }, { 'size': fontSizeArr }],
                    [{ 'lineHeight': ['0.5', '0.6', '0.7', '0.8', '0.9', '1', '1.1', '1.2', '1.3', '1.5', '1.7', '2', "3", "4", "5", "6"] }],
                    [{ 'script': 'sub' }, { 'script': 'super' }],
                    [{ 'header': [1, 2, 3, false] }],
                    //['blockquote', 'code-block'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    [{ 'indent': '-1' }, { 'indent': '+1' }],
                    [{ 'direction': 'rtl' }],
                    [{ 'align': [] }],
                    ['link'],  // Removed the Image
                    ['clean']
                ]
            }
        });

        // Custom handler for line height to support lists
        quill.getModule('toolbar').addHandler('lineHeight', function (value: string) {
            const range = quill.getSelection();
            if (range) {
                const format = quill.getFormat(range);
                if (format.list) {
                    const lines = quill.getLines(range);
                    lines.forEach((line: any) => {
                        LineHeightBlot.applyToList(line.domNode, value);
                    });
                } else {
                    quill.format('lineHeight', value);
                }
            }
        });

        // Add Custom DropDown For "ql-align" toolbar option
        this.addCustomDropDownForAlignOption();
        // "addQuillTooltips" method must be called after "addCustomDropDownForAlignOption"
        // Add The ToolTips For Toolbar Options
        this.addQuillTooltips();

        // const value = this.propInfo?.find(x => x.name == 'innerHTML')?.value || '';
        // let dom = new DOMParser().parseFromString(value, 'text/html');
        // let htmlValue= dom.querySelector('div.ql-editor.section')?.innerHTML;
        const value = this.propInfo?.find(x => x.name == 'delta')?.value || '';
        quill.root.setAttribute('data-default-size', '12px'); // optional for tracking
        setTimeout(() => {
            if (value == '' || Object.keys(value).length === 0) {
                quill.setSelection(0, 0);
                quill.format('size', '12px');
                quill.format('font', 'Primer Print')
                quill.format('lineHeight', '1')
            }
        }, 0);

        quill.setContents(value, 'api')

        const buttonContainer = document.createElement('div');
        Object.assign(buttonContainer.style, {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px'
        });

        const saveButton = document.createElement('button');
        Object.assign(saveButton.style, {
            padding: '8px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
        });
        saveButton.textContent = 'Save';
        saveButton.title = 'Save';

        const cancelButton = document.createElement('button');
        Object.assign(cancelButton.style, {
            padding: '8px 16px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
        });
        cancelButton.textContent = 'Cancel';
        cancelButton.title = 'Cancel';

        saveButton.addEventListener('click', () => {
            this.textContent = quill.root.innerHTML;
            div.innerHTML = `<div style="padding:5px 5px" class='ql-editor section'>${this.textContent}</div>`;
            try {
                this.applyComputedStyleFromClass(div);
            }
            catch {
                console.log("failed to apply quill class properties as inline style")
            }
            this.propInfo?.forEach((x) => {
                if (x.name == 'innerHTML') {
                    x.value = div.innerHTML;
                }
                if (x.name == 'delta') {
                    x.value = DomHelper.cloneObj(quill.getContents());
                }
            });

            let sm = StateManager.getInstance();
            let oldState = sm.getState();
            oldState.sections.forEach(sec => {
                if (sec.id === this.id) {
                    sec.propInfo = [...this.propInfo!];
                }
            });
            sm.setState({
                changeType: changeType.UPDATE_SECTION,
                state: oldState,
                sectionIds: [this.id],
                targetEl: div
            });
            parentElement.removeChild(dialog);
            parentElement.removeChild(backdrop);
            document.body.style.overflow = '';
            DomHelper.focusViewport();
        });

        cancelButton.addEventListener('click', () => {
            parentElement.removeChild(dialog);
            parentElement.removeChild(backdrop);
            document.body.style.overflow = '';
            DomHelper.focusViewport();
        });

        buttonContainer.appendChild(cancelButton);
        buttonContainer.appendChild(saveButton);
        dialog.appendChild(buttonContainer);
    }

    async fetchCssClassesAndProperties(): Promise<Record<string, Record<string, string>>> {
        try {
            const cssText = QuillCSS.defaultCSS;
            return this.parseCssText(cssText);
        } catch (error) {
            console.error("Error fetching CSS:", error);
            return {};
        }
    }

    parseCssText(cssText: string): Record<string, Record<string, string>> {
        const cssClasses: Record<string, Record<string, string>> = {};
        const regex = /([^{]+)\s*\{([^}]+)\}/g; // Matches selectors and properties

        let match;
        while ((match = regex.exec(cssText)) !== null) {
            const selectors = match[1].split(",").map(s => s.trim()); // Handle multiple selectors
            const propertiesText = match[2].trim();
            const properties: Record<string, string> = {};

            propertiesText.split(";").forEach(prop => {
                const [key, value] = prop.split(":").map(part => part.trim());
                if (key && value) {
                    properties[key] = value;
                }
            });

            // Store properties for each selector
            for (const selector of selectors) {
                if (selector.startsWith(".")) {
                    cssClasses[selector] = properties;
                }
            }
        }

        return cssClasses;
    }

    getDeclaredPropertiesFromDesignerCSS(className: string): Set<string> {
        const props = new Set<string>();
        for (const css in this.layoutDesignerClasses) {
            if (css.split(',').map(s => s.trim()).some(s => new RegExp(`\\.${className}$`).test(s))) {
                const style = this.layoutDesignerClasses[css];
                for (const prop in style) {
                    props.add(prop);
                }
            }
        }
        return props;
    }

    applyComputedStyleFromClass(el: HTMLElement): void {
        const computedStyle = window.getComputedStyle(el);
        const classList = Array.from(el.classList);

        const allDeclaredProps = new Set<string>();

        classList.forEach(className => {
            const declaredProps = this.getDeclaredPropertiesFromDesignerCSS(className);
            declaredProps.forEach(prop => allDeclaredProps.add(prop));
        });

        allDeclaredProps.forEach(prop => {
            const value = computedStyle.getPropertyValue(prop);
            if (prop === 'list-style' || prop === 'list-style-type') {
                // Preserve list styles for ul, ol, and li
                if (el.tagName === 'UL' || el.tagName === 'OL' || el.tagName === 'LI') {
                    el.style.setProperty(prop, value, 'important');
                }
            } else {
                el.style.setProperty(prop, value);
            }
        });

        Array.from(el.children).forEach(child => {
            if (child instanceof HTMLElement) {
                this.applyComputedStyleFromClass(child);
            }
        });
    }
    getFontArray() {
        const values = [
            7, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60, 64, 68, 72, 76, 80, 84, 88, 92, 96, 100
        ]

        return values.map(x => `${x}px`);
    }

}



